/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect, useRef } from 'react';
import OutsideClickHandler from 'react-outside-click-handler';
import { Button, Checkbox, Chip, ChipContainer, ChipType } from '@festo-ui/react';
import { OptionsType, OptionType } from './types';
import {
  getOptionLabel as getOptionLabelBuiltin,
  getOptionValue as getOptionValueBuiltin,
  getOptionTracking as getOptionTrackingBuiltin,
} from './builtins';
import { econdaClickEvent } from '../../../Econda';
import './multiselect-box.scss';
import InfoButtonPopup from '../../InfoButtonPopup';
import { useTranslation } from '../../../hooks/useTranslation';
import NoResultsFound from '../../NoResultsFound/NoResultsFound';
import { isMobile } from '../../../helper';
const classNames = require('classnames');

//onInputChange that let you use handlers on changes to the textbox
//isComboBox that display the textbox if it's true
export interface MultiselectBoxProps {
  filterKey: string;
  econdaName: string;
  options: OptionsType;
  onChange?: (values: OptionsType) => void;
  onInputChange?: (inputValue: string, { action }: any) => void;
  label?: string;
  applyText?: string;
  cancelText?: string;
  allValue?: boolean;
  allText?: string;
  getOptionValue?: typeof getOptionValueBuiltin;
  getOptionLabel?: typeof getOptionLabelBuiltin;
  getOptionTracking?: typeof getOptionTrackingBuiltin;
  defaultValue?: OptionsType;
  values?: OptionsType;
  isCombobox?: boolean;
  placeholder?: string;
  infoButtonDescription?: string;
  tableRef: React.RefObject<HTMLDivElement>;
}
//onInputChange and isCombobox initialization
//inputValue state initialization to have memory of what's been written in the textbox
const MultiselectBox: React.FC<MultiselectBoxProps> = ({
  econdaName,
  options,
  onChange,
  onInputChange,
  label,
  applyText,
  allText = 'All',
  allValue = false,
  getOptionLabel = getOptionLabelBuiltin,
  getOptionValue = getOptionValueBuiltin,
  getOptionTracking = getOptionTrackingBuiltin,
  defaultValue = [],
  values = [],
  isCombobox = false,
  placeholder,
  infoButtonDescription,
  tableRef,
}) => {
  const [optionsWrapper, setOptionsWrapper] = useState({ open: false, action: '' });
  const [stateValues, setStateValues] = useState(defaultValue);
  const [inputValue, setInputValue] = useState('');
  const [selectedMediaFilterList, setSelectedMediaFilterList] = useState<string[]>([]);
  const [selectedTempMediaFilterList, setSelectedTempMediaFilterList] = useState<string[]>([]);
  const [temporaryValues, setTemporaryValues] = useState<OptionsType>(JSON.parse(JSON.stringify(defaultValue)));
  const [isSortingActive, setIsSortingActive] = useState(false);
  const buttonEl = useRef<HTMLDivElement>(null);
  const listItemRef = useRef(Array.from({ length: options.length }, () => React.createRef<HTMLLIElement>()));
  const t = useTranslation();

  const prevOpenRef = useRef<boolean>(false);

  useEffect(() => {
    setStateValues(values);
    setTemporaryValues(JSON.parse(JSON.stringify(values)));
  }, [values]);

  useEffect(() => {
    setIsSortingActive(stateValues.length === 0 ? false : true);

    const mediaFilterTKList: string[] = [];
    stateValues.forEach((value) => mediaFilterTKList.push(value.id));
    setSelectedMediaFilterList(mediaFilterTKList);

    const mediaTempFilterTKList: string[] = [];
    temporaryValues.forEach((value) => mediaTempFilterTKList.push(value.id));
    setSelectedTempMediaFilterList(mediaTempFilterTKList);
  }, [stateValues, temporaryValues]);

  useEffect(() => {
    prevOpenRef.current = optionsWrapper.open;
  });
  const prevOpen = prevOpenRef.current;

  useEffect(() => {
    if (!prevOpen && optionsWrapper.open) {
      if (optionsWrapper.action === 'key') {
        const index =
          stateValues.length > 0
            ? options.findIndex(
                (option) => getOptionLabel(option, t) === getOptionLabel(stateValues[stateValues.length - 1], t),
              )
            : 0;
        if (index >= 0) {
          focusNextElement(index);
        }
      } else if (optionsWrapper.action === 'click') {
        handleButtonFocus(true);
      }
    }
  }, [prevOpen, optionsWrapper, stateValues, options, getOptionLabel, t]);

  function handleButtonFocus(blur?: boolean): void {
    if (buttonEl && buttonEl.current) {
      if (blur) {
        buttonEl.current.blur();
      } else {
        buttonEl.current.focus();
      }
    }
  }

  const focusNextElement = (nextIndex: number): void => {
    if (listItemRef && listItemRef.current && nextIndex) {
      const nextItem = listItemRef.current[nextIndex].current;
      if (nextItem !== null) {
        nextItem.focus();
      }
    }
  };

  const applyChanges = (newTemporaryValues: OptionsType) => {
    econdaClickEvent(`${econdaName} filter`, 'filters', 'primary button', 'apply button');
    setStateValues(JSON.parse(JSON.stringify(newTemporaryValues)));
    if (onChange) {
      onChange(newTemporaryValues);
    }
    //clears the textbox when the changes are applied
    if (isCombobox) {
      const event = { target: { value: '' }, type: 'clear' };
      handleInputChange(event);
    }
    setOptionsWrapper({ action: '', open: false });
  };
  const clearAll = () => {
    econdaClickEvent(`${econdaName} filter`, 'filters', 'primary button', 'clear button');
    if (onChange) {
      onChange([]);
    }
    //clears the textbox when the changes are applied
    if (isCombobox) {
      const event = { target: { value: '' }, type: 'clear' };
      handleInputChange(event);
    }
  };

  const undoChanges = () => {
    setOptionsWrapper({ action: '', open: false });
    setTemporaryValues(JSON.parse(JSON.stringify(stateValues)));
    //clears the textbox when changes are canceled
    if (isCombobox) {
      const event = { target: { value: '' }, type: 'clear' };
      handleInputChange(event);
    }
  };

  const handleListClick = (e: React.MouseEvent<HTMLLIElement, MouseEvent>, option: OptionType) => {
    e.preventDefault();
    handleOptionChange(option);
  };
  const handleOptionChange = (option: OptionType, changeStateValues?: boolean) => {
    const candidate = getOptionValue(option);
    const newTemporaryValues = temporaryValues.concat([]);
    const index = newTemporaryValues.findIndex((opt) => getOptionValue(opt) === candidate);
    if (index === -1) {
      newTemporaryValues.push(option);
      const id = option.id;
      econdaClickEvent(`${econdaName} filter`, 'filters', 'check box', id + '-' + getOptionTracking(option));
    } else {
      newTemporaryValues.splice(index, 1);
    }
    setTemporaryValues(newTemporaryValues);
    if (changeStateValues) {
      applyChanges(newTemporaryValues);
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    const validKeys = ['Enter', 'ArrowUp', 'ArrowDown'];
    if (validKeys.includes(event.key)) {
      event.preventDefault();
      setOptionsWrapper((prevOptionsWrapper) => ({ action: 'key', open: !prevOptionsWrapper.open }));
    }
  };

  const handleListKeyDown = (event: React.KeyboardEvent<HTMLLIElement>, option: OptionType, index: number): void => {
    const arrowKeys = ['ArrowDown', 'ArrowUp'];
    const validKeys = ['Enter'];
    if (arrowKeys.concat(validKeys).includes(event.key)) {
      event.preventDefault();
    }

    switch (event.key) {
      case 'ArrowDown':
        focusNextElement((index + 1) % options.length);
        break;
      case 'ArrowUp': {
        let nextIndex = (index - 1) % options.length;
        if (nextIndex < 0) {
          nextIndex += options.length;
        }
        focusNextElement(nextIndex);
        break;
      }
      case 'Escape': {
        undoChanges();
        handleButtonFocus();
        break;
      }
      case 'Enter':
        handleOptionChange(option);
        break;
      default:
      // do nothing
    }
  };

  const handleChipClick = (e: React.MouseEvent<HTMLElement, MouseEvent>, option: OptionType) => {
    e.stopPropagation();
    handleOptionChange(option, true);
  };

  const selectClassName = classNames('select', { focus: optionsWrapper }, { empty: !optionsWrapper });

  //handler of the input change
  function handleInputChange(e: any) {
    setInputValue(e.target.value);
    if (onInputChange) {
      onInputChange(e.target.value, e.type);
    }
  }

  const handleApply = () => {
    if (tableRef && tableRef.current && isMobile(window)) {
      const y = tableRef.current.getBoundingClientRect().top + window.pageYOffset + -65;
      setTimeout(() => window.scrollTo({ top: y, behavior: 'smooth' }), 0);
    }
    applyChanges(temporaryValues);
  };

  return (
    <div className="multiselect-box container-full">
      <div className="row align-items-baseline">
        <div className="col-10 select-wrapper">
          <div
            role="button"
            tabIndex={0}
            className={selectClassName}
            onClick={() =>
              setOptionsWrapper((prevOptionsWrapper) => ({ action: 'click', open: !prevOptionsWrapper.open }))
            }
            onKeyDown={handleKeyPress}
            ref={buttonEl}
          >
            <div className="select-content">
              <span
                className={`input-span ${optionsWrapper.open && 'input-span-open'}`}
                onClick={() =>
                  econdaClickEvent(`${econdaName} filter`, 'filters', 'dropdown', `${econdaName} dropdown`)
                }
              >
                <input
                  className={`fwe-input-text ${optionsWrapper.open && 'fwe-input-text-open'}`}
                  disabled={true}
                  autoComplete="off"
                  type="text"
                  placeholder={label}
                />
                <i
                  className={`fwe-icon ${optionsWrapper.open ? 'fwe-icon-arrows-expand' : 'fwe-icon-arrows-collapse'}`}
                ></i>
              </span>
            </div>
          </div>
          {optionsWrapper.open && (
            <div className="select-options-wrapper">
              <OutsideClickHandler
                onOutsideClick={handleApply}
                children={
                  <div className="select-options-container">
                    <ul className="select-options-list pt-0">
                      <li>
                        <span
                          onClick={() =>
                            econdaClickEvent(
                              `${econdaName} filter`,
                              'filters',
                              'search bar',
                              `${econdaName} search bar`,
                            )
                          }
                        >
                          <input
                            className={`fwe-input-text ${isCombobox ? 'search-icon' : ''}`}
                            value={inputValue}
                            onChange={handleInputChange}
                            disabled={false}
                            autoComplete="off"
                            type="text"
                            placeholder={placeholder}
                            autoFocus
                          />
                        </span>
                      </li>
                      {options.length > 0 ? (
                        options.map((option, i) => {
                          if (
                            isSortingActive &&
                            stateValues.some((opt) => getOptionValue(opt) === getOptionValue(option))
                          ) {
                            const isChecked = selectedTempMediaFilterList.includes(option.id);

                            return (
                              <li
                                ref={listItemRef.current[i]}
                                className="select-option"
                                role="option"
                                aria-selected={true}
                                // eslint-disable-next-line react/no-array-index-key
                                key={`${getOptionValue(option)}${i}`}
                                onClick={(e) => handleListClick(e, option)}
                                tabIndex={0}
                                onKeyDown={(e) => handleListKeyDown(e, option, i)}
                              >
                                <Checkbox
                                  tabIndex={-1}
                                  checked={isChecked}
                                  id={`${getOptionValue(option)}${i}`}
                                  onChange={() => undefined}
                                >
                                  {getOptionLabel(option, t).name}{' '}
                                  <span className="font-color-gray">{getOptionLabel(option, t).complement}</span>
                                </Checkbox>
                              </li>
                            );
                          }
                          return null;
                        })
                      ) : (
                        <NoResultsFound
                          iconSize="fwe-icon-3x"
                          noResultsTitle="noResultsFound"
                          noResultsMessage="noResultsFilterMessage"
                        />
                      )}
                      {isSortingActive && stateValues.length > 0 && <div className="divider"></div>}
                      {options.map((option, i) => {
                        const isRepeated = selectedMediaFilterList.includes(option.id);

                        if (isRepeated) return null;

                        return (
                          <li
                            ref={listItemRef.current[i]}
                            className="select-option"
                            role="option"
                            aria-selected={temporaryValues.some(
                              (opt) => getOptionValue(opt) === getOptionValue(option),
                            )}
                            // eslint-disable-next-line react/no-array-index-key
                            key={`${getOptionValue(option)}${i}`}
                            onClick={(e) => handleListClick(e, option)}
                            tabIndex={0}
                            onKeyDown={(e) => handleListKeyDown(e, option, i)}
                          >
                            <Checkbox
                              className={`${getOptionLabel(option, t).name.length > 30 && 'regular-checkbox'}`}
                              tabIndex={-1}
                              checked={temporaryValues.some((opt) => getOptionValue(opt) === getOptionValue(option))}
                              id={`${getOptionValue(option)}${i}`}
                              onChange={() => undefined}
                            >
                              {getOptionLabel(option, t).name}{' '}
                              <span className="font-color-gray">{getOptionLabel(option, t).complement}</span>
                            </Checkbox>
                          </li>
                        );
                      })}
                    </ul>
                    <div className="buttons fwe-mt-2">
                      {options.length > 0 && (
                        <>
                          <Button
                            icon="fwe-icon fwe-icon-arrows-reset"
                            className="fwe-mb-2 clear-button button-space"
                            primary
                            onClick={() => clearAll()}
                          >
                            {t('clearAll')}
                          </Button>
                          <Button className="fwe-mb-2 button-space apply-button" primary onClick={handleApply}>
                            {applyText}
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                }
              ></OutsideClickHandler>
            </div>
          )}

          <ChipContainer>
            {stateValues.length > 0 || !allValue ? (
              stateValues.map((option, index) => (
                <Chip
                  icon="menu-close"
                  selected
                  type={ChipType.Category}
                  // eslint-disable-next-line react/no-array-index-key
                  key={`${getOptionValue(option)}${index}`}
                  onClick={(e) => handleChipClick(e, option)}
                >
                  {getOptionLabel(option, t).name} {getOptionLabel(option, t).complement}
                </Chip>
              ))
            ) : (
              <Chip selected>{allText}</Chip>
            )}
          </ChipContainer>
        </div>
        <div
          className="col-1"
          onClick={() => econdaClickEvent(`${econdaName} filter`, 'filters', 'tool tip', `${econdaName} tool tip`)}
        >
          <InfoButtonPopup>
            <p>{infoButtonDescription}</p>
          </InfoButtonPopup>
        </div>
      </div>
    </div>
  );
};

export default MultiselectBox;
